import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api, { apiError, setToken, clearToken, getToken } from "../lib/api";

const storedUser = (() => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
})();

// --- Thunks ------------------------------------------------------------
export const login = createAsyncThunk(
  "auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/api/auth/login.php", credentials);
      const { token, user } = data.data;
      setToken(token);
      localStorage.setItem("user", JSON.stringify(user));
      window.dispatchEvent(new Event("userUpdated"));
      return user;
    } catch (error) {
      return rejectWithValue(apiError(error, "Login failed."));
    }
  }
);

export const loadUser = createAsyncThunk(
  "auth/loadUser",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/api/auth/me.php");
      localStorage.setItem("user", JSON.stringify(data.data));
      return data.data;
    } catch (error) {
      return rejectWithValue(apiError(error, "Session expired."));
    }
  }
);

// --- Slice -------------------------------------------------------------
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: storedUser,
    token: getToken(),
    loading: false,
    error: null,
    initialized: false, // becomes true after first loadUser attempt
  },
  reducers: {
    logout(state) {
      clearToken();
      localStorage.removeItem("user");
      state.user = null;
      state.token = null;
      window.dispatchEvent(new Event("userUpdated"));
    },
    setUser(state, action) {
      state.user = action.payload;
      localStorage.setItem("user", JSON.stringify(action.payload));
      window.dispatchEvent(new Event("userUpdated"));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.token = getToken();
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.initialized = true;
      })
      .addCase(loadUser.rejected, (state) => {
        state.user = null;
        state.token = null;
        state.initialized = true;
      });
  },
});

export const { logout, setUser } = authSlice.actions;

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;

// Permission helper (mirrors backend Auth::can)
export const hasPermission = (user, page, action = "view") => {
  if (!user) return false;
  if (user.is_super) return true;
  const perms = user.permissions || {};
  return Boolean(perms[page] && perms[page][action]);
};

export default authSlice.reducer;
